import { useEffect, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { bringToFront, flashWindow } from "../../web/components/RecordWindow";
import { WINDOW_KINDS, type WindowKind } from "./meta";

/*
 * The Price List's windows live in the page's address, one of each kind open at a time:
 *   ?item=<uuid>&itemTab=pricing         an item's window (item=new: adding one)
 *   ?supplier=<uuid>&supplierTab=prices  a supplier's window (supplier=new: adding one)
 *   ?bundle=<uuid>&bundleTab=contents    a bundle's window (bundle=new: adding one)
 *   ?group=<id>&groupTab=items           a group's window (&rename: just made, with a
 *                                        made-up name ready to type over)
 * Opening one changes the address of whatever page is showing (a Price List list, Home...), so
 * it opens over that page (WindowHost.tsx draws them, on any page). Opening another of the same
 * kind replaces it; windows of different kinds can be open together.
 */

/**
 * Windows with unsaved changes (by kind): opening another record in one is stopped, the window
 * flashes, and it's told, with `proceed` to open it after all (its changes discarded). Closing
 * isn't stopped here: windows ask before closing themselves.
 */
const guards = new Map<string, (proceed: () => void) => void>();

/** While `unsaved`, this kind's window can't be swapped for another record without asking (`onBlocked`). */
export function useUnsavedGuard(kind: string, unsaved: boolean, onBlocked: (proceed: () => void) => void) {
    const latest = useRef(onBlocked);
    latest.current = onBlocked;
    useEffect(() => {
        if (!unsaved) return;
        guards.set(kind, (proceed) => latest.current(proceed));
        return () => {
            guards.delete(kind);
        };
    }, [kind, unsaved]);
}

/**
 * A window's unsaved changes to show in its Needs attention (as "Unsaved pricing"...): each once you've moved off its tab (on
 * it, its save bar's right there), and from then on until it's saved or cancelled, even back on its tab. `changes`: each kind
 * of change, whether it's unsaved and the tab it's on. Gives the kinds to show.
 */
export function useLeftUnsaved<K extends string>(tab: string, changes: Record<K, { unsaved: boolean; tab: string }>): ReadonlySet<K> {
    const [left, setLeft] = useState<ReadonlySet<K>>(new Set());
    const kinds = Object.keys(changes) as K[];
    // What's unsaved where, as one key: worked out again only when that (or the tab) changes.
    const key = kinds.map((k) => `${k}:${changes[k].unsaved ? changes[k].tab : ""}`).join(",");
    useEffect(() => {
        setLeft((was) => {
            const next = new Set(kinds.filter((k) => changes[k].unsaved && (was.has(k) || tab !== changes[k].tab)));
            return next.size === was.size && [...next].every((k) => was.has(k)) ? was : next;
        });
        // `key` stands for `changes` (a new object each render).
    }, [tab, key]);
    return left;
}

/** Opening and closing the Price List's windows, over the page that's showing. */
export function usePriceListWindows() {
    const [location, navigate] = useLocation();
    const params = new URLSearchParams(useSearch());

    /**
     * Change some of the address's windows (null closes one, or drops a key), keeping the page and the
     * rest. A window with unsaved changes isn't swapped for another record (useUnsavedGuard), unless `force`.
     */
    const set = (changes: Record<string, string | null>, force = false) => {
        const blocked = force ? undefined : WINDOW_KINDS.find((kind) => changes[kind] && changes[kind] !== params.get(kind) && guards.has(kind));
        if (blocked) {
            guards.get(blocked)?.(() => set(changes, true));
            flashWindow(blocked);
            return;
        }
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
        }
        const query = next.toString();
        navigate(location + (query ? "?" + query : ""), { replace: true });
        // Each window opened comes to the front, even one already open (behind another, or showing that record already).
        // Just after: a new window has shown itself by then.
        const opened = WINDOW_KINDS.filter((kind) => changes[kind]);
        if (opened.length) setTimeout(() => opened.forEach((kind) => bringToFront(kind)));
    };

    /**
     * A record in its kind's window, replacing the one there: on the tab that one's on (the same tab, record to record), else
     * its Details tab (no tab in the address: each window's first).
     */
    const open = (kind: WindowKind, id: string, more: Record<string, string | null> = {}) =>
        set({ [kind]: id, [`${kind}Tab`]: params.get(kind) ? params.get(`${kind}Tab`) : null, ...more });

    return {
        params,
        set,
        openItem: (uuid: string) => open("item", uuid),
        openSupplier: (uuid: string) => open("supplier", uuid),
        openBundle: (uuid: string) => open("bundle", uuid),
        /** `rename`: it was just made with a made-up name, so its name is ready to type over (on its Details tab). */
        openGroup: (id: string, options?: { rename?: boolean }) => (options?.rename ? set({ group: id, groupTab: null, rename: "1" }) : open("group", id, { rename: null })),
    };
}
